// ============================================================
// TMR Portal Admin — international phone numbers (E.164), pure
// ============================================================
// The backend is the authority: it stores E.164 and validates it (16 U1). These helpers exist so the
// screen can read and write that format without ever inferring a country — the defect the previous mask
// had, cutting at eleven digits and reading the `55` of `+55...` as an area code.
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'BR';

const regionNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });

export const PHONE_COUNTRIES = getCountries()
  .map((country) => ({
    value: country,
    label: `${regionNames.of(country) ?? country} (+${getCountryCallingCode(country)})`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

/** The country the value itself declares; Brazil for as long as it declares none. */
export function countryFromPhone(value: string): CountryCode {
  return parsePhoneNumberFromString(value)?.country ?? DEFAULT_PHONE_COUNTRY;
}

/**
 * What the field shows while someone types. A value with no `+` takes the selected country's calling
 * code — an explicit choice made in the form, never a guess about where the data came from.
 */
export function formatInternationalPhoneInput(value: string, country = DEFAULT_PHONE_COUNTRY): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const international = trimmed.startsWith('+')
    ? trimmed
    : `+${getCountryCallingCode(country)}${trimmed.replace(/\D/g, '')}`;
  return new AsYouType().input(international);
}

/**
 * Swaps the calling code, keeping the national digits already typed. Changing the country must not erase
 * the number: someone who picks the wrong country first corrects the country, not the whole phone.
 */
export function changePhoneCountry(value: string, country: CountryCode): string {
  const parsed = parsePhoneNumberFromString(value);
  const nationalDigits = parsed?.nationalNumber ?? value.replace(/\D/g, '');
  return formatInternationalPhoneInput(`+${getCountryCallingCode(country)}${nationalDigits}`);
}

/** Empty is valid — the phone is always optional (16 §2). Filled in, it has to be a real number. */
export function isValidInternationalPhone(value: string): boolean {
  if (!value.trim()) return true;
  return parsePhoneNumberFromString(value)?.isValid() ?? false;
}

/** The value the API receives, or null — including when it is invalid, which validation already blocked. */
export function toE164Phone(value: string): string | null {
  if (!value.trim()) return null;
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.isValid() ? parsed.number : null;
}

/** The stored E.164, spaced out to be read. Used when a form opens on an existing record. */
export function displayPhone(value?: string | null): string {
  if (!value) return '';
  return new AsYouType().input(value);
}
